import { useEffect, useState } from 'react';
import { apiRequest } from '@/lib/api/client';
import type { TeacherStudent } from '@/features/teacher/types';
type Team = {
  id: string;
  title: string;
  description?: string;
  deadline?: string;
  status: string;
  member_ids: string[];
};
export function CourseTeams({
  code,
  group,
  students,
  teacher = false,
}: {
  code: string;
  group?: string;
  students?: TeacherStudent[];
  teacher?: boolean;
}) {
  const [teams, setTeams] = useState<Team[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [deadline, setDeadline] = useState('');
  const [members, setMembers] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let active = true;
    setTeams([]);
    setMembers([]);
    setTitle('');
    setError('');
    if (!code || (teacher && !group)) return;
    const query = `discipline_code=${encodeURIComponent(code)}${teacher ? `&group_code=${encodeURIComponent(group!)}` : ''}`;
    void apiRequest<Team[]>(`/${teacher ? 'teacher' : 'course-student'}/team-projects?${query}`, {
      channel: 'platform',
    })
      .then((rows) => {
        if (active) setTeams(rows);
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [code, group, teacher]);
  const create = async () => {
    setSaving(true);
    setError('');
    try {
      const team = await apiRequest<Team>('/teacher/team-projects', {
        method: 'POST',
        channel: 'platform',
        body: {
          discipline_code: code,
          group_code: group,
          title,
          description,
          deadline,
          member_ids: members,
        },
      });
      setTeams((prev) => [...prev, team]);
      setTitle('');
      setMembers([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось создать команду');
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="space-y-3 text-sm">
      <h3>Учебные командные проекты</h3>
      {error && <p role="alert">{error}</p>}
      {teacher && group && (
        <details>
          <summary>Создать командный проект</summary>
          <input
            className="bg-slate-900 text-white border border-slate-700"
            aria-label="Название команды"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Название"
          />
          <textarea
            className="bg-slate-900 text-white border border-slate-700"
            aria-label="Задача команды"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <input
            className="bg-slate-900 text-white border border-slate-700"
            aria-label="Срок проекта"
            type="date"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
          />
          {students
            ?.filter((s) => s.user_id)
            .map((s) => (
              <label key={s.id} className="block">
                <input
                  className="bg-slate-900 text-white border border-slate-700"
                  type="checkbox"
                  checked={members.includes(s.user_id!)}
                  onChange={(e) =>
                    setMembers((prev) =>
                      e.target.checked
                        ? [...prev, s.user_id!]
                        : prev.filter((id) => id !== s.user_id),
                    )
                  }
                />
                {s.full_name} · {s.student_code}
              </label>
            ))}
          <button
            disabled={saving || !title.trim() || !members.length}
            onClick={() => void create()}
          >
            Создать проект
          </button>
        </details>
      )}
      {!teams.length && <p>Командные проекты не назначены.</p>}
      {teams.map((team) => (
        <article key={team.id} className="rounded-xl border border-slate-700 p-4">
          <h4>{team.title}</h4>
          <p>{team.description}</p>
          <p>
            {team.status} · {team.deadline || 'Срок не задан'} · Участников:{' '}
            {team.member_ids.length}
          </p>
        </article>
      ))}
    </section>
  );
}
